import {Menu} from '@mantine/core';
import {Stack} from 'src/design/atoms/Stack';
import type {MRT_ColumnDef} from 'mantine-react-table';
import React, {useMemo} from 'react';
import type {
  AssetRelatedFormSimpleResponse,
  FormStateEnum} from 'src/api/ProactApiClient';
import {
  FormSubmissionScopeEnum,
  ModuleEnum,
  PermissionOperationEnum
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {NoticePopover} from 'src/design/molecules/NoticePopover';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {getFormPillConfig} from 'src/app/forms/formHelpers';
import {useGetAssetRelatedFormsListV2Query} from 'src/redux/queries/asset';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useFormSubmissionExport} from 'src/shared/hooks/useFormSubmissionExport';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface AssetFormSubmissionListProps {
  assetId: number;
}

export const AssetFormSubmissionList: React.FC<
  AssetFormSubmissionListProps
> = ({assetId}) => {
  const navigate = useAppNavigate();

  const columns = useMemo<MRT_ColumnDef<AssetRelatedFormSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'formName',
        header: 'Form Name',
      },
      {
        accessorKey: 'internalIdentifier',
        header: 'Identifier',
      },
      {
        accessorKey: 'shortDescription',
        header: 'Description',
      },
      {
        accessorKey: 'fieldLabel',
        header: 'Field Label',
      },
      {
        accessorKey: 'creatorName',
        header: 'Created By',
      },
      {
        accessorKey: 'createdAt',
        header: 'Created On',
        Cell: ({cell}) => <>{cell.getValue<Date>().toString().toShortDate()}</>,
      },
      {
        accessorKey: 'updatedAt',
        header: 'Update On',
        Cell: ({cell}) => <>{cell.getValue<Date>().toString().toShortDate()}</>,
      },
      {
        accessorKey: 'state',
        header: 'Status',
        enableColumnFilter: false,
        enableColumnFilterModes: false,

        Cell: ({cell}) => {
          const cellValue = cell.getValue<FormStateEnum>();

          const pillConfigs = getFormPillConfig(cellValue);

          return (
            <>
              {pillConfigs.map((pillOptions, index) => (
                <Pill
                  key={index}
                  text={pillOptions.text}
                  color={pillOptions.color}
                />
              ))}
            </>
          );
        },
      },
    ],
    []
  );

  const tableState = useTableState({
    identifier: 'assets-forms',
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'updatedAt', desc: true}],
    defaultShowColumnFilters: true,
  });
  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetAssetRelatedFormsListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
        assetId: assetId,
      },
      {refetchOnMountOrArgChange: true}
    );
  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {handleExport, isExporting} = useFormSubmissionExport(
    FormSubmissionScopeEnum.AssetRelated,
    {assetId},
    {filters, filterFns, sorting}
  );

  const handleNavigateToAsset = (obj: AssetRelatedFormSimpleResponse) =>
    navigate(
      PathUtility.FormSubmission(
        obj.formDefinitionId,
        obj.formSubmissionId,
        'content'
      )
    );

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActionMenuItems: ({row}) => (
        <>
          <Menu.Item onClick={() => handleNavigateToAsset(row.original)}>
            View
          </Menu.Item>
        </>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <PermissionGuard
              module={ModuleEnum.Forms}
              operation={PermissionOperationEnum.Export}>
              <TableToolbar.Action
                variant="export"
                onClick={handleExport}
                loading={isExporting}
              />
            </PermissionGuard>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <Stack gap="xs">
        <NoticePopover
          label="Permissions affecting data"
          title="Permissions Affecting Data"
          description="This list only shows form submissions you have permission to access."
          secondaryText="Contact your administrator if you need access to additional forms."
        />
        {table}
      </Stack>
    </ContentWrapper>
  );
};
